import { useEffect } from "react";
import { usePasswordResetViewModel } from "@/viewModels/usePasswordResetViewModel";
import { ForgotPasswordForm } from "@/ui/components/ForgotPasswordForm";
import { AuthLayout } from "@/ui/layouts";
import type { ForgotPasswordFormData } from "@/schemas/passwordResetSchemas";

/**
 * Forgot Password Page
 *
 * Pure page component following MVVM pattern.
 * - No business logic (all in ViewModel)
 * - Single useEffect for lifecycle (onMount)
 * - Delegates all user actions to ViewModel
 *
 * Story: 3.3.b - Password Reset Frontend UI
 *
 * Features:
 * - Email input for password reset request
 * - Delegates form submission to ViewModel
 * - Shows success message after code is sent
 * - Provides "Back to Login" navigation
 */
export default function ForgotPasswordPage() {
  const { state, actions, computed, onMount } = usePasswordResetViewModel();

  // Only allowed useEffect - lifecycle management
  useEffect(() => {
    const cleanup = onMount();
    return cleanup;
  }, [onMount]);

  // Pure view - delegate form submission to ViewModel
  const handleSubmit = async (formData: ForgotPasswordFormData) => {
    await actions.requestReset(formData.email);
  };

  return (
    <AuthLayout>
        <ForgotPasswordForm
          onSubmit={handleSubmit}
          onBackToLogin={actions.navigateToLogin}
          isLoading={state.isRequesting}
          error={computed.hasError ? computed.errorMessage : null}
        />
      </AuthLayout>
  );
}
